/** AUTO search form — compact base criteria + collapsible groups. */
import React from "react";
import DarkSelect from "../home/DarkSelect";
import DarkMultiSelect from "../home/DarkMultiSelect";
import LocationPicker from "../home/LocationPicker";
import { locationFromParams, locationPatch } from "../../lib/searchQuery";
import { Field, NumberSelect, Section, ToggleChip, fmtEur, fmtKm } from "./fields";

const PRICE_STEPS = [1000, 2500, 5000, 7500, 10000, 15000, 20000, 25000, 30000, 40000, 50000, 75000, 100000, 150000];
const KM_STEPS = [5000, 10000, 20000, 30000, 50000, 75000, 100000, 125000, 150000, 175000, 200000, 250000];
const KW_STEPS = [44, 55, 66, 74, 85, 96, 110, 125, 140, 155, 185, 220, 300];
/** Backend contract: max. 5 makes and 5 models per request. */
const MAX_MAKES = 5;
const MAX_MODELS = 5;
const years = () => {
  const y = [];
  for (let i = new Date().getFullYear(); i >= 1990; i -= 1) y.push(i);
  return y;
};

export default function AutoSearchForm({
  params, set, fo, models, t, foStatus = {}, modelsStatus = {}, modelsByMake = {},
  locText = "", setLocText,
}) {
  const statusProps = (st) => ({
    loading: !!st.loading,
    error: !!st.error,
    onRetry: st.retry,
    loadingLabel: t("search.loadingOptions"),
    errorLabel: t("search.optionsError"),
    retryLabel: t("search.retry"),
  });
  const makes = (fo.makes || []).map((m) => ({
    value: m.value, label: `${m.value} (${m.count})`,
  }));
  const selectedMakes = params.make ? String(params.make).split(",").filter(Boolean) : [];
  // Models of EVERY selected make, grouped by make → removing a make
  // removes exactly its models.
  const modelOptions = (models || []).map((m) => ({
    value: m.value, label: m.label, count: m.count,
  }));
  const selectedModels = params.model ? String(params.model).split(",").filter(Boolean) : [];
  const featureOpts = (fo.features || []).map((f) => ({
    value: f.value,
    label: `${t(`enums:features.${f.value}`, { defaultValue: f.value })} (${f.count})`,
  }));
  const colorOpts = (fo.colors || []).map((c) => ({ value: c.value, label: `${c.value} (${c.count})` }));
  // Only values the API actually delivers become options (no dead steps).
  const numericFacet = (rows) => (rows || [])
    .map((r) => Number(r.value))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  const doorSteps = numericFacet(fo.doors);
  const seatSteps = numericFacet(fo.seats);
  // Parity with native: a vocabulary the API does NOT deliver is not
  // offered at all — while it loads or failed the field stays mounted so
  // the retry path remains reachable.
  const vocabPending = !!foStatus.loading || !!foStatus.error;
  const selColors = params.color ? String(params.color).split(",") : [];
  const selFeatures = params.features ? String(params.features).split(",") : [];
  const cnt = (keys) => keys.filter((k) => params[k]).length;

  const onMakes = (vals) => {
    if (vals.length > MAX_MAKES) return;
    const removed = selectedMakes.filter((m) => !vals.includes(m));
    let nextModels = selectedModels;
    if (removed.length && selectedModels.length) {
      const removedKeys = new Set(removed.flatMap((m) => modelsByMake[m] || []));
      const keptKeys = new Set(vals.flatMap((m) => modelsByMake[m] || []));
      nextModels = selectedModels.filter((k) => !removedKeys.has(k) || keptKeys.has(k));
    }
    if (!vals.length) nextModels = [];
    set({
      make: vals.length ? vals.join(",") : null,
      model: nextModels.length ? nextModels.join(",") : null,
    });
  };

  return (
    <div className="space-y-4">
      {/* Base criteria — always visible, generous grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={t("hero.make")}>
          <DarkMultiSelect
            values={selectedMakes}
            testid="search-make"
            options={makes}
            placeholder={t("hero.anyMake")}
            searchPlaceholder={t("search.searchMake")}
            selectedLabel={(n) => (n <= 2 ? selectedMakes.join(", ") : t("search.nSelected", { count: n }))}
            resetLabel={t("search.reset")}
            onChange={onMakes}
            {...statusProps(foStatus)}
          />
          {selectedMakes.length >= MAX_MAKES && (
            <span className="block text-[10px] text-zinc-500" data-testid="search-make-max">
              {t("search.maxMakes", { count: MAX_MAKES })}
            </span>
          )}
        </Field>
        <Field label={t("hero.model")}>
          <DarkMultiSelect
            values={selectedModels}
            testid="search-model"
            options={modelOptions}
            placeholder={selectedMakes.length ? t("hero.anyModel") : t("search.modelsNeedMake")}
            searchPlaceholder={t("search.searchModel")}
            selectedLabel={(n) => (n <= 2
              ? selectedModels
                .map((v) => (modelOptions.find((o) => o.value === v)?.label || v).replace(" · ", " ").replace(/\s*\(\d+\)$/, ""))
                .join(", ")
              : t("search.nSelected", { count: n }))}
            resetLabel={t("search.reset")}
            onChange={(vals) => {
              if (vals.length > MAX_MODELS) return;
              set({ model: vals.length ? vals.join(",") : null });
            }}
            {...statusProps(selectedMakes.length ? modelsStatus : {})}
          />
          {selectedModels.length >= MAX_MODELS && (
            <span className="block text-[10px] text-zinc-500" data-testid="search-model-max">
              {t("search.maxModels", { count: MAX_MODELS })}
            </span>
          )}
        </Field>
        <Field label={t("hero.location")}>
          <LocationPicker
            vertical="auto"
            country={params.country}
            testid="search-location"
            value={locationFromParams(params)}
            text={locText}
            onTextChange={setLocText}
            onChange={(loc) => set(locationPatch(loc))}
          />
        </Field>
        <Field label={t("search.priceTo")}>
          <NumberSelect
            value={params.price_max}
            steps={PRICE_STEPS}
            anyLabel={t("hero.any")}
            customLabel={t("search.customValue")}
            format={fmtEur}
            testid="search-price-max"
            onChange={(v) => set({ price_max: v })}
          />
        </Field>
        <Field label={t("search.firstRegFrom")}>
          <NumberSelect
            value={params.year_min}
            steps={years()}
            anyLabel={t("hero.any")}
            customLabel={t("search.customValue")}
            testid="search-year-min"
            onChange={(v) => set({ year_min: v })}
          />
        </Field>
        <Field label={t("search.kmTo")}>
          <NumberSelect
            value={params.mileage_max}
            steps={KM_STEPS}
            anyLabel={t("hero.any")}
            customLabel={t("search.customValue")}
            format={fmtKm}
            testid="search-mileage-max"
            onChange={(v) => set({ mileage_max: v })}
          />
        </Field>
      </div>

      <Section
        title={t("search.groupTech")}
        testid="search-group-tech"
        count={cnt(["fuel", "gearbox", "body_type", "power_min", "power_max", "price_min", "year_max", "mileage_min"])}
      >
        <Field label={t("hero.fuel")}>
          <DarkSelect
            value={params.fuel || ""}
            testid="search-fuel"
            options={[{ value: "", label: t("hero.any") }, ...(fo.fuels || []).map((f) => ({
              value: f.value, label: `${t(`enums:fuel.${f.value}`, { defaultValue: f.value })} (${f.count})`,
            }))]}
            onChange={(v) => set({ fuel: v || null })}
          />
        </Field>
        <Field label={t("hero.gearbox")}>
          <DarkSelect
            value={params.gearbox || ""}
            testid="search-gearbox"
            options={[{ value: "", label: t("hero.any") }, ...(fo.gearboxes || []).map((g) => ({
              value: g.value, label: `${t(`enums:gearbox.${g.value}`, { defaultValue: g.value })} (${g.count})`,
            }))]}
            onChange={(v) => set({ gearbox: v || null })}
          />
        </Field>
        <Field label={t("hero.bodyType")}>
          <DarkSelect
            value={params.body_type || ""}
            testid="search-body-type"
            options={[{ value: "", label: t("hero.any") }, ...(fo.body_types || []).map((b) => ({
              value: b.value, label: `${t(`enums:body_type.${b.value}`, { defaultValue: b.value })} (${b.count})`,
            }))]}
            onChange={(v) => set({ body_type: v || null })}
          />
        </Field>
        <Field label={t("search.powerFrom")}>
          <NumberSelect value={params.power_min} steps={KW_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={(n) => `${n} kW`} testid="search-power-min" onChange={(v) => set({ power_min: v })} />
        </Field>
        <Field label={t("search.powerTo")}>
          <NumberSelect value={params.power_max} steps={KW_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={(n) => `${n} kW`} testid="search-power-max" onChange={(v) => set({ power_max: v })} />
        </Field>
        <Field label={t("search.priceFrom")}>
          <NumberSelect value={params.price_min} steps={PRICE_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={fmtEur} testid="search-price-min" onChange={(v) => set({ price_min: v })} />
        </Field>
        <Field label={t("search.firstRegTo")}>
          <NumberSelect value={params.year_max} steps={years()} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-year-max" onChange={(v) => set({ year_max: v })} />
        </Field>
        <Field label={t("search.kmFrom")}>
          <NumberSelect value={params.mileage_min} steps={KM_STEPS} anyLabel={t("hero.any")} customLabel={t("search.customValue")} format={fmtKm} testid="search-mileage-min" onChange={(v) => set({ mileage_min: v })} />
        </Field>
      </Section>

      <Section
        title={t("search.groupCondition")}
        testid="search-group-condition"
        count={cnt(["accident_free", "owners_max", "seller_type", "price_reduced", "new_days", "rated"]) + (params.quality === "deals" ? 1 : 0)}
      >
        <div className="col-span-full flex flex-wrap gap-2">
          <ToggleChip
            active={!!params.accident_free}
            testid="search-accident-free"
            onClick={() => set({ accident_free: params.accident_free ? null : true })}
          >
            {t("search.accidentFree")}
          </ToggleChip>
          <ToggleChip active={!!params.price_reduced} testid="search-price-reduced" onClick={() => set({ price_reduced: params.price_reduced ? null : true })}>
            {t("quick.priceReduced")}
          </ToggleChip>
          <ToggleChip active={!!params.new_days} testid="search-new-days" onClick={() => set({ new_days: params.new_days ? null : 7 })}>
            {t("quick.newDaysChip")}
          </ToggleChip>
          <ToggleChip active={!!params.rated} testid="search-rated" onClick={() => set({ rated: params.rated ? null : true })}>
            {t("search.ratedOnly")}
          </ToggleChip>
          <ToggleChip active={params.quality === "deals"} testid="search-deals-only" onClick={() => set({ quality: params.quality === "deals" ? null : "deals" })}>
            {t("quick.dealsOnly")}
          </ToggleChip>
        </div>
        <Field label={t("search.ownersMax")}>
          <NumberSelect value={params.owners_max} steps={[1, 2, 3, 4]} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-owners-max" onChange={(v) => set({ owners_max: v })} />
        </Field>
        <Field label={t("hero.sellerType")}>
          <DarkSelect
            value={params.seller_type || ""}
            testid="search-seller-type"
            options={[
              { value: "", label: t("hero.any") },
              { value: "private", label: t("hero.seller_private") },
              { value: "dealer", label: t("hero.seller_dealer") },
            ]}
            onChange={(v) => set({ seller_type: v || null })}
          />
        </Field>
      </Section>

      <Section
        title={t("search.groupEquipment")}
        testid="search-group-equipment"
        count={selFeatures.length + selColors.length + cnt(["doors_min", "seats_min"])}
      >
        {(featureOpts.length > 0 || vocabPending) && (
        <Field label={t("hero.features")}>
          <DarkMultiSelect
            values={selFeatures}
            testid="search-features"
            options={featureOpts}
            placeholder={t("search.anyFeatures")}
            searchPlaceholder={t("search.searchFeature")}
            selectedLabel={(n) => t("search.nSelected", { count: n })}
            resetLabel={t("search.reset")}
            onChange={(vals) => set({ features: vals.length ? vals.join(",") : null })}
            {...statusProps(foStatus)}
          />
        </Field>
        )}
        {(colorOpts.length > 0 || vocabPending) && (
        <Field label={t("search.color")}>
          <DarkMultiSelect
            values={selColors}
            testid="search-color"
            options={colorOpts}
            placeholder={t("hero.any")}
            searchPlaceholder={t("search.searchColor")}
            selectedLabel={(n) => t("search.nSelected", { count: n })}
            resetLabel={t("search.reset")}
            onChange={(vals) => set({ color: vals.length ? vals.join(",") : null })}
            {...statusProps(foStatus)}
          />
        </Field>
        )}
        {(doorSteps.length > 0 || vocabPending) && (
        <Field label={t("search.doorsMin")}>
          <NumberSelect value={params.doors_min} steps={doorSteps} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-doors-min" onChange={(v) => set({ doors_min: v })} />
        </Field>
        )}
        {(seatSteps.length > 0 || vocabPending) && (
        <Field label={t("search.seatsMin")}>
          <NumberSelect value={params.seats_min} steps={seatSteps} anyLabel={t("hero.any")} customLabel={t("search.customValue")} testid="search-seats-min" onChange={(v) => set({ seats_min: v })} />
        </Field>
        )}
      </Section>
    </div>
  );
}
